import React, { useContext, useState } from 'react';
import Modal from 'react-modal';
import Slider from 'react-slick';
import { CountryCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { P, H4 } from '@components/shared/Typography';
import { SCIENCE_GOGGLES_SVG } from '@constants';
import CloseIcon from '@images/icon-x.svg';
import LandingTick from '@images/landing-tick.svg';
import medBelinda from '@images/med-belinda-coker.png';
import medMelanie from '@images/med-melanie-bone.png';
import decoration from '@images/medical-decoration-top.png';
import { getSupplyChainPricing } from '@utils/supplyChain';

export default function TamponsScience() {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const supplyChainPricing = getSupplyChainPricing();
  const [medBoardOpen, setMedBoardOpen] = useState(false);
  const [clinicalTrialsOpen, setClinicalTrialsOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '60vh',
      padding: '0',
      border: '0',
      borderRadius: '0',
      background: '#FFFBE7'
    },
    overlay: {
      background: `${Colors.navyBlue}25`
    }
  };

  const medBoardMembers = [
    {
      title: 'Dr. Nicola Tempest',
      text: t('components.landing-science.nicola')
    },
    {
      title: 'Prof. Dharani Hapangama',
      text: t('components.landing-science.dharani')
    },
    {
      title: 'Prof. Louise Kenny',
      text: t('components.landing-science.louise')
    },
    {
      title: 'Prof. Iain Buchan',
      text: t('components.landing-science.iain')
    },
    {
      title: 'Dr. Melanie Bone',
      text: t('components.landing-science.melanie')
    },
    {
      title: 'Dr. Belinda Coker',
      text: t('components.landing-science.belinda')
    },
    {
      title: 'Mr. Benjamin Viaris de Lesegno',
      text: t('components.landing-science.benjamin')
    },
    {
      title: 'Prof. Douglas Kell',
      text: t('components.landing-science.douglas')
    },
    { title: 'Martin Varsavsky', text: t('components.landing-science.martin') },
    {
      title: 'Miss Claire Mellon',
      text: t('components.landing-science.claire')
    }
  ];

  const preClinicalList = [
    t('components.landing-science.impact'),
    t('components.landing-science.bio'),
    t('components.landing-science.toxic'),
    t('components.landing-science.absorbency'),
    t('components.landing-science.fibre')
  ];

  const humanTrialsList = [t('components.landing-science.cbd'), t('components.landing-science.research'), t('components.landing-science.microbiome')];

  function closeModal() {
    setMedBoardOpen(false);
    setClinicalTrialsOpen(false);
    setPricingOpen(false);
  }

  const settings = {
    arrows: false,
    dots: true,
    fade: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  let slides = [
    {
      image: medBelinda,
      quote: t('components.landing-science.belinda-quote'),
      name: 'Dr. Belinda Coker',
      position: t('components.landing-science.doctor')
    },
    {
      image: medMelanie,
      quote: t('components.landing-science.melanie-quote'),
      name: 'Dr. Melanie Bone',
      position: t('components.landing-science.member')
    }
  ];

  if (country === CountryCode.US) {
    slides = [
      {
        image: medMelanie,
        quote: t('components.landing-science.melanie-quote-alt'),
        name: 'Dr. Melanie Bone',
        position: t('components.landing-science.member')
      }
    ];
  }

  return (
    <>
      <Container>
        <Left>
          <LeftInner>
            <IconBoxWrap>
              <IconBox>
                <ImgWrap>
                  <img src={SCIENCE_GOGGLES_SVG} width={140} />
                </ImgWrap>
                <TextWrap>
                  <H4Styled>{t('components.landing-science.science-behind')}</H4Styled>
                  <PStyled>{t('components.landing-science.we-work')}</PStyled>
                </TextWrap>
              </IconBox>
            </IconBoxWrap>
          </LeftInner>
        </Left>
        <Right>
          <RightInner>
            <PatternBar src={decoration} width="580" height="26" />
            <SliderWrap>
              <Slider {...settings}>
                {slides.map((slide: any, index: number) => (
                  <Slide key={index}>
                    <QuoteImage src={slide.image} width="315" height="152" />
                    <Quote>{slide.quote}</Quote>
                    <QuoteCite>
                      <Name>{slide.name}</Name>
                      <Position>{slide.position}</Position>
                    </QuoteCite>
                  </Slide>
                ))}
              </Slider>
            </SliderWrap>
          </RightInner>
        </Right>
      </Container>
      <Modal isOpen={medBoardOpen} onAfterOpen={() => null} onRequestClose={closeModal} style={customStyles} contentLabel="Medical Board">
        <ModalHeader>
          <h2>{t('components.landing-science.medical-board')}</h2>
          <button onClick={closeModal}>
            <CloseIcon />
          </button>
        </ModalHeader>
        <ModalBody>
          <MemberText $mods={['center']}>{t('components.landing-science.medical-advisory')}</MemberText>
          <MedBoardMembers>
            {medBoardMembers.map((item: any, index: number) => (
              <MedBoardMember key={index}>
                <MemberTitle>{item.title}</MemberTitle>
                <MemberText>{item.text}</MemberText>
              </MedBoardMember>
            ))}
          </MedBoardMembers>
          <ModalEndClose>
            <LinkStyled onClick={closeModal}>{t('components.close')}</LinkStyled>
          </ModalEndClose>
        </ModalBody>
      </Modal>

      <Modal isOpen={clinicalTrialsOpen} onAfterOpen={() => null} onRequestClose={closeModal} style={customStyles} contentLabel="Clinical Trials">
        <ModalHeader>
          <h2>{t('components.landing-science.trials')}</h2>
          <button onClick={closeModal}>
            <CloseIcon />
          </button>
        </ModalHeader>
        <ModalBody>
          <MemberText $mods={['center']}>{t('components.landing-science.studies')}</MemberText>
          <ModalBodyBold>{t('components.landing-science.pre')}</ModalBodyBold>
          <DescriptionList>
            {preClinicalList.map((line: string, index: number) => (
              <ListItem key={index}>
                <ListTick />
                <ListText>{line}</ListText>
              </ListItem>
            ))}
          </DescriptionList>
          <ModalBodyBold>{t('components.landing-science.human')}</ModalBodyBold>
          <DescriptionList>
            {humanTrialsList.map((line: string, index: number) => (
              <ListItem key={index}>
                <ListTick />
                <ListText>{line}</ListText>
              </ListItem>
            ))}
          </DescriptionList>
          <ModalEndClose>
            <LinkStyled onClick={closeModal}>{t('components.close')}</LinkStyled>
          </ModalEndClose>
        </ModalBody>
      </Modal>

      <Modal isOpen={pricingOpen} onAfterOpen={() => null} onRequestClose={closeModal} style={customStyles} contentLabel="Pricing">
        <ModalHeader>
          <h2>{t('components.landing-science.pricing')}</h2>
          <button onClick={closeModal}>
            <CloseIcon />
          </button>
        </ModalHeader>
        <ModalBody>
          <MemberText $mods={['center']}>{t('components.landing-science.chain-costs')}</MemberText>
          <PriceTable>
            <TableHeader>{t('components.landing-science.naked')}</TableHeader>
            <TableRow>
              <PriceName>{t('components.landing-science.tampons')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.tampons[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.sanitisation')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.sanitisation[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.testing')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.testing[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.packaging')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.packaging[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.cargo')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.cargo[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableDivide />
            <TableRow>
              <PriceName>{t('components.landing-science.true-cost')}*</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.trueCostNaked[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRowBold>
              <PriceName>{t('components.landing-science.our-price')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.consumerNaked[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRowBold>
          </PriceTable>

          <PriceTable>
            <TableHeader>{t('components.landing-science.cramp')}</TableHeader>
            <TableRow>
              <PriceName>{t('components.landing-science.tampons')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.tampons[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.sanitisation')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.sanitisation[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.cramp-extract')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.cbd[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.testing')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.testing[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.packaging')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.packaging[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRow>
              <PriceName>{t('components.landing-science.cargo')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.cargo[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableDivide />
            <TableRow>
              <PriceName>{t('components.landing-science.true-cost')}*</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.trueCostCBD[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRow>
            <TableRowBold>
              <PriceName>{t('components.landing-science.our-price')}</PriceName>
              <PriceTotal>
                {currency.symbol}
                {supplyChainPricing.consumerCBD[currency.letterCode]?.toFixed(2)}
              </PriceTotal>
            </TableRowBold>
          </PriceTable>
          <ModalEndClose>
            <LinkStyled onClick={closeModal}>{t('components.close')}</LinkStyled>
          </ModalEndClose>
        </ModalBody>
      </Modal>
    </>
  );
}

const Container = styled.div`
  display: flex;
  padding-bottom: 100px;
  max-width: 1600px;
  margin: 0 auto;
  animation: fadein 2s;

  @media only screen and (max-width: 1600px) {
    max-width: 100%;
  }

  @media only screen and (max-width: 768px) {
    flex-direction: column-reverse;
    height: auto;
    margin-top: -90px;
    padding-bottom: 30px;
  }
`;

const Left = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 100px 20px 0;

  @media only screen and (max-width: 768px) {
    width: 100%;
    padding: 60px 30px 0;
  }
`;

const LeftInner = styled.div`
  max-width: 100%;
  text-align: center;
`;

const IconBoxWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 530px;

  @media (max-width: 1000px) {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const IconBox = styled.div`
  display: flex;
  flex: 100%;
  margin: 0 0 100px;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  flex-grow: 0;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex: 100%;
    width: 100%;
    max-width: 100%;
    padding: 0;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ImgWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  & > svg {
    width: 140px;
  }
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 60px;
  max-width: 330px;
  text-align: left;
  align-items: flex-start;

  @media (max-width: 768px) {
    text-align: center;
    margin-left: 0;
    max-width: 100%;
    align-items: center;
  }
`;

const H4Styled = styled(H4)`
  font-family: 'Domaine';
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  line-height: 34px;
  letter-spacing: 0;
  margin-bottom: 20px;
`;

const PStyled = styled(P)`
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 27px;
  letter-spacing: 0;
  margin-bottom: 30px;
`;

const LinkStyled = styled.a`
  font-family: 'BrownStd';
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  border-bottom: 2px solid #003b1b;
  margin-bottom: 20px;
  display: inline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    opacity: 0.5;
  }
`;

const Right = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 100px 20px 0;

  @media only screen and (max-width: 768px) {
    width: 100%;
    padding: 60px 30px 0;
    margin-top: 0;
  }
`;

const RightInner = styled.div`
  width: 100%;
  max-width: 580px;
  text-align: center;
  background: #e5f2e3;
  border: 2px solid #003b1b;
  border-top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const PatternBar = styled.img`
  width: 100%;
  height: auto;
`;

const SliderWrap = styled.div`
  padding: 40px 40px 70px;
  display: flex;
  flex-direction: column;
  width: 80%;
  max-width: 1200px;
  text-align: center;

  & .slick-dots {
    bottom: -30px;
  }

  @media only screen and (max-width: 768px) {
    padding: 40px 20px 60px;
  }
`;

const Slide = styled.div`
  max-width: 100%;
  outline: none;
`;

const QuoteImage = styled.img`
  margin: 0 auto 30px;
  height: auto;
`;

const Quote = styled(P)`
  font-family: BrownStd;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
  letter-spacing: 0;
  margin-bottom: 40px;

  @media only screen and (max-width: 768px) {
    font-size: 16px;
    line-height: 26px;
  }
`;

const QuoteCite = styled.div``;

const Name = styled.h5`
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 23px;
  letter-spacing: 0;
  color: #003b1b;
`;

const Position = styled.h5`
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  letter-spacing: 0;
  color: #003b1b;
`;

const ModalHeader = styled.div`
  padding: 32px;
  background: #c5e3c1;
  position: relative;

  h2 {
    font-family: 'BrownStd';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 2px;
    text-align: center;
    text-transform: uppercase;
    color: #003b1b;
  }

  button {
    background: transparent;
    position: absolute;
    right: 30px;
    top: 50%;
    border: 0;
    padding: 0;
    height: 22px;
    width: 22px;
    margin-top: -11px;
    outline: none;
    cursor: pointer;

    &:hover {
      opacity: 0.5;
    }
  }
`;

const ModalBody = styled.div`
  padding: 30px 40px;

  @media only screen and (max-width: 768px) {
    padding: 20px;
  }
`;

const ModalBodyBold = styled(PStyled)`
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  color: #003b1b;
  margin-bottom: 14px;
`;

const MedBoardMembers = styled.div`
  margin: 40px 0;
`;

const MedBoardMember = styled.div`
  text-align: center;
  padding: 0 10%;
`;

const MemberText = styled(PStyled)`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  color: #003b1b;
`;

const MemberTitle = styled(MemberText)`
  font-weight: bold;
  margin-bottom: 2px;
`;

const ModalEndClose = styled.div`
  text-align: center;
`;

const DescriptionList = styled.ul`
  list-style: none;
  margin: 0 0 50px;
  padding: 0;
`;

const ListItem = styled.li`
  line-height: 1.2;
  padding: 0;
  margin: 10px 0 0;
  position: relative;
  padding-left: 30px;

  &:first-child {
    margin-top: 0;
  }

  @media only screen and (max-width: 768px) {
    margin-top: 6px;
  }
`;

const ListTick = styled(LandingTick)`
  position: absolute;
  top: 2px;
  left: 0;
`;

const ListText = styled.span`
  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 23px;
    letter-spacing: 0;
  }
`;

const PriceTable = styled.div`
  border-bottom: 1px solid #f9d07c;
  margin-bottom: 40px;
  padding-bottom: 10px;
`;

const TableHeader = styled.div`
  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 1.5px;
  text-align: center;
  text-transform: uppercase;
  border-bottom: 1px solid #f9d07c;
  padding: 30px;
  color: #003b1b;
  margin-bottom: 10px;
`;

const TableRow = styled.div`
  padding: 12px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TableRowBold = styled(TableRow)`
  font-weight: bold;
`;

const TableDivide = styled.div`
  width: 100%;
  height: 1px;
  background: #f9d07c;
  margin: 10px 0;
`;

const PriceName = styled(P)`
  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
`;

const PriceTotal = styled(P)`
  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  text-align: right;
`;
